/**
 * 이상한 방 - 봉투 연출의 시각표와 단계 전이 ("언제")
 *
 * 근거 문서: 로드맵 확정안 3.3절(연출 시퀀스) · 0절 판정표 10번 · 6절("근접 실패 색 깜빡" 기각)
 *           `ui/juice.ts` 머리말(정리 권한은 타이머 하나 · `Math.random` 0)
 *
 * `ui/envelope.ts`에서 갈라져 나왔다. 조각들(머리·게이지·스택·카드·세리머니·버튼 줄)은 "무엇을 어떻게 그리는가"만 알고,
 * **언제 무엇이 일어나는가는 이 파일 한 곳이 정한다** - 그래서 `setTimeout`은 여기에만 있다.
 *
 * ## 시각표가 둘로 갈렸다 - 개봉 전은 같고, 개봉 후는 갈린다 (갈라 둔 이유는 `envelopeCeremony.ts` 머리말)
 *
 *  - **개봉 전 3.28초(`ENVELOPE_TIMELINE`, `envelopeTimeline.ts`)는 네 등급이 글자 하나까지 같다.** 길이도 소리도 떨림도 같고,
 *    등급을 아는 물건은 정직한 바늘과 릴, 그 둘이 가리키는 같은 값 하나뿐이다(실제 `gradeRoll`). 등급 색·등급
 *    글자는 DOM에 **없다** - 찢는 순간에 처음 앉는다. 흘리는 척만 하는 것은 6절이 기각한 근접 실패 속임수다
 *  - **개봉 후(`GRADE_CEREMONY`, `envelopeCeremony.ts`)는 등급이 정한다.** N 0.35초(툭) ~ SSR 2.84초.
 *    화려함은 예고가 아니라 보상이다
 *
 * ## 개봉 전이 1.4초에서 3.28초가 됐다 - 바늘 한 번이 아니라 릴이 돈다
 *
 * 사람이 두 번 말했다: "우린 누르자마자 뾱 뽑히는 것 같다." 짧았던 것은 뒷부분이 아니라 **앞부분**이라 앞당김
 * 0.36초 + 릴 2.6초 + 정지 0.32초로 나눴다. 릴이 시간을 채우되 정보는 흘리지 않는 이유는 `envelopeReel.ts`,
 * 소리 쪽 절반은 `envelopeReelSound.ts` 머리말이다. 둘 다 읽어라.
 *
 * ## 정리의 권한은 한 곳이다 - 예약은 둘이되 슬롯이 갈려 있다
 *
 * `juice.ts`와 같은 규약이다. `animationend`·`transitionend`를 듣지 않는다 - 움직임 줄이기에서는 그 이벤트가
 * 오지 않고, 그러면 카드가 영영 뒤집히지 않는다. 화면을 접는 것(`stop`)도 예약을 걷는 것으로 끝난다.
 *
 * 슬롯이 둘인 이유는 하나다. **단계 경계가 소리 커서에 매달리면 안 된다.** 정지로 넘어가는 예약을 열몇 개짜리
 * 소리 사슬 끝에 두면, 예약을 1초에 하나로 묶는 숨은 탭에서 2.6초 릴이 십수 초가 된다(그쪽 머리말). 그래서
 * 단계는 절대 시각의 예약 하나로 남기고 소리는 자기 슬롯만 쓴다. 둘 다 걷는 것은 여전히 `clearAll` 하나다.
 */

import type { EnvelopeSoundName } from '../audio/sounds'
import { prefersReducedMotion } from '../core/env'
import type { Pity } from '../game/economy/envelope'
import { ENVELOPE_TABLE, type EnvelopeTable } from '../game/economy/envelopeTable'
import { t } from '../i18n'

import type { EnvelopeActions } from './envelopeActions'
import type { EnvelopeCard, EnvelopeCardView } from './envelopeCard'
import { ceremonyFor, tenStackTotalMs, type EnvelopeCeremonyLayer, type GradeCeremony } from './envelopeCeremony'
import {
  armCard as armCardInto,
  ceremonyPlanOf,
  featureOf,
  lowerOf,
  paintPity as paintPityInto,
  paintTexts as paintTextsInto,
  playGrade as playGradeOf,
  resetVisual as resetVisualInto,
  tableOf,
  tearCard as tearCardInto,
  type EnvelopeDrawState,
} from './envelopeDraw'
import type { EnvelopeGauge } from './envelopeGauge'
import type { EnvelopeHead } from './envelopeHead'
import { reelSpinPeriodMs } from './envelopeReel'
import { createReelSoundRun } from './envelopeReelSound'
import { revealOrder, type EnvelopeStack } from './envelopeStack'
import { ENVELOPE_TIMELINE, reelSpinMs } from './envelopeTimeline'

/* --- 타입 ---------------------------------------------------------------------- */
export type EnvelopeView = Readonly<{
  /** 뽑은 순서 그대로 넘긴다 - 연출 순서는 이 화면이 정한다(`revealOrder`). */
  cards: readonly EnvelopeCardView[]
  /** 이번에 쓴 핀. 다시 보기(`?pull=`)는 0이라 칩에서 떨어지는 숫자가 없다. */
  spent: number
  /** 열기 전의 보유 핀. 칩이 여기서 출발한다 - `pinsAfter + spent`로 역산하지 않는다(환급이 있으면 다른 수다). */
  pinsBefore: number
  /** 다 열고 난 뒤의 보유 핀 · 계정 탄창. */
  pinsAfter: number
  magazinesAfter: number
  /**
   * 첫 장을 열기 전의 피티. 게이지가 여기서 출발해 마지막 카드의 `pityAfter`로 간다. `null`이면 모르는 값
   * (이력 밖 회차의 다시 보기)이고 게이지를 통째로 감춘다 - 새 계정 값으로 그리면 게이지가 스탬프와 다른 말을 한다.
   */
  pityBefore: Pity | null
  /** 다음 열기가 가능한가. 안 되면 [한 장 더]가 몇 판이면 열리는지 말한다. */
  next: Readonly<{ single: boolean; ten: boolean; roundsToAfford: number }>
  /** `?pull=`의 다시 보기. 저장이 움직이지 않으므로 [한 장 더]가 없다. */
  replay?: boolean
  table?: EnvelopeTable
}>

/**
 * 연출의 단계. `stack`은 열 장에서만, `dim`은 SR·SSR에서만 지나간다. `hold`가 개봉 전의 마지막이고
 * `idle`은 화면이 접혀 있는 상태다.
 */
export type EnvelopePhase = 'idle' | 'rise' | 'stack' | 'sweep' | 'hold' | 'dim' | 'tear' | 'stamp' | 'done'

/** 단계 전이가 움직이는 조각들. 전부 `envelope.ts`가 만들어 넘긴다 - 여기서는 DOM을 만들지 않는다. */
export type EnvelopeParts = Readonly<{
  /** `.ev-root` · 크롭마크(SR 이상에서 한 번 켜진다) · "탭하면 건너뛴다" 한 줄 · 건너뛰기 버튼(연출 중에만 선다). */
  root: HTMLElement
  crops: HTMLElement
  hint: HTMLElement
  skip: HTMLElement
  head: EnvelopeHead
  gauge: EnvelopeGauge
  stack: EnvelopeStack
  card: EnvelopeCard
  ceremony: EnvelopeCeremonyLayer
  actions: EnvelopeActions
  /** 소리. 이름만 넘긴다 - 재생은 엔진의 몫. */
  play: (name: EnvelopeSoundName) => void
}>

export type EnvelopePhases = Readonly<{
  /** 뷰를 앉히고 연출을 시작한다. 빈 뷰는 곧바로 버튼, 움직임 줄이기는 컷. */
  show: (view: EnvelopeView) => void
  /** 예약을 걷고 `idle`로. 그 뒤로는 아무 단계도 진행되지 않는다. */
  stop: () => void
  /** 어느 시각에서든 곧바로 끝 상태로. 탭과 Enter가 여기로 온다. */
  jumpToDone: () => void
  current: () => EnvelopePhase
  /** 뷰가 있는가. 언어 전환이 다시 그릴지 물을 때 쓴다. */
  hasView: () => boolean
  /** 언어에 따라 바뀌는 글자 전부. `show`와 언어 전환이 같은 함수를 쓴다. */
  paintTexts: () => void
}>

/* --- 예약 슬롯 ------------------------------------------------------------------ */
/** 예약 하나를 쥔다. 새로 걸면 앞의 것을 지운다(`juice.ts` 규약). 이 화면은 이 슬롯을 둘 쓴다(머리말). */
function createSlot(): Readonly<{ at: (run: () => void, ms: number) => void; clear: () => void }> {
  let id: number | null = null
  const clear = (): void => {
    if (id === null) return
    window.clearTimeout(id)
    id = null
  }
  return {
    at: (run, ms) => {
      clear()
      id = window.setTimeout(() => {
        id = null
        run()
      }, ms)
    },
    clear,
  }
}

/* --- 본체 ---------------------------------------------------------------------- */
export function createEnvelopePhases(parts: EnvelopeParts): EnvelopePhases {
  const { root, hint, skip, head, gauge, stack, card, ceremony, actions, play } = parts

  let phase: EnvelopePhase = 'idle'
  let lastView: EnvelopeView | null = null
  /** 이번 뷰의 연출 순서(마지막이 주인공)와, 열 장에서 지금까지 연 장 수. */
  let ordered: readonly EnvelopeCardView[] = []
  let stacked = 0

  /** 그릴 순간의 상태 한 덩이. 그리기 편(`envelopeDraw.ts`)이 읽기만 한다. */
  function drawState(): EnvelopeDrawState {
    return { view: lastView, ordered, phase }
  }

  const table = (): EnvelopeTable => tableOf(drawState())
  /** 주인공 카드(한 장이면 그 한 장, 열 장이면 최고 등급)와 그를 뺀 나머지(스택의 몫). */
  const feature = (): EnvelopeCardView | null => featureOf(drawState())
  const lower = (): readonly EnvelopeCardView[] => lowerOf(drawState())
  /** 이번 회차의 후반부 표. 움직임 줄이기면 등급과 무관하게 하나다. */
  const plan = (): GradeCeremony => ceremonyPlanOf(drawState())
  /** 단계 슬롯 하나 · 소리 슬롯 하나. 갈라 둔 이유는 머리말에 있다. */
  const phaseSlot = createSlot()
  const soundSlot = createSlot()
  const schedule = phaseSlot.at
  /** 걷는 자리는 언제나 둘 다 걷는다 - 하나만 걷으면 접은 화면에서 소리가 이어진다. */
  function clearAll(): void {
    phaseSlot.clear()
    soundSlot.clear()
  }

  /**
   * 릴이 도는 동안의 소리. 타이머는 내주지 않고 소리 슬롯과 시계만 빌려 준다(`envelopeReelSound.ts` 머리말).
   * 시계를 넘기는 것은 예약이 밀렸을 때 남은 간격을 다시 재기 위해서다 - 안 그러면 소리가 조금씩 늦는다.
   */
  const reelSound = createReelSoundRun({ play, schedule: soundSlot.at, now: () => performance.now() })

  /* --- 그리기는 `envelopeDraw.ts`가 한다. 여기는 지금 상태를 넘길 뿐이다. --- */
  const paintTexts = (): void => paintTextsInto(parts, drawState())
  const paintPity = (stage: 'before' | 'after'): void => paintPityInto(parts, drawState(), stage)
  /** 카드 한 장이 찢기는 순간. 주인공 한 장과 아홉 장이 **같은 경로**를 쓴다(사본 금지). */
  const tearCard = (entry: EnvelopeCardView, ceremonyPlan: GradeCeremony, position: number): void =>
    tearCardInto(parts, drawState(), entry, ceremonyPlan, position)
  const playGrade = (entry: EnvelopeCardView): void => playGradeOf(parts, entry.draw.grade)
  /** 아홉 장 구간에서 지금 열리고 있는 장(다 열었으면 `undefined`)과 그 장이 쓸 표. */
  const stackEntry = (): EnvelopeCardView | undefined => lower()[stacked]
  const stackPlan = (entry: EnvelopeCardView): GradeCeremony => ceremonyFor(entry.draw.grade, prefersReducedMotion())

  /**
   * 다음 한 장을 위해 화면을 개봉 전으로 되돌린다(카드 · 크롭마크 · 세리머니 레이어 + 리플로 한 번).
   * 무엇을 왜 되돌리는지는 `envelopeDraw.ts`의 `armCard` 머리말에 있다 - 여기는 "언제"만 안다.
   */
  const armCard = (): void => armCardInto(parts)

  /** 화면을 첫 상태로. 연출이 쥐고 있던 눈금(넘긴 장 수)도 여기서 되돌린다. */
  function resetVisual(): void {
    resetVisualInto(parts, drawState())
    stacked = 0
  }

  /* --- 단계 전이: 개봉 전 (전 등급 공통) --- */
  function enterRise(): void {
    phase = 'rise'
    const view = lastView
    if (view === null) return
    card.rise()
    play('envelopeOpen')
    // 값이 빠지는 것을 먼저 보인다. 뽑고 나서 빠지면 "뽑았는데 왜 줄었지"가 된다.
    if (view.spent > 0) head.pins.drop(view.spent)
    head.pins.set(view.pinsAfter)
    schedule(ordered.length > 1 ? enterStack : enterSweep, ENVELOPE_TIMELINE.riseMs)
  }

  /**
   * 열 장의 앞 아홉. **한 장씩 큰 카드 자리에서 개봉한다**(P5). 전에는 "미니 칩 하나 뒤집기"였고 아홉이
   * 1~2초에 다 지나갔다 - 사람은 그것을 두고 "누르자마자 열 장 결과가 바로 보인다"고 했다. 지금은 한 장이
   * 주인공과 **같은 경로**(`tearCard` → 스탬프)를 지나고 길이는 그 등급의 세리머니 하나다(`stepRevealMs`).
   * 어두워짐만 빠진다 - 아홉 번 꺼졌다 켜지는 화면은 고조가 아니라 깜빡임이고, 어둠은 마지막 한 장의 것이다.
   * 길어진 만큼 건너뛰기가 항상 서 있다(`.ev-skip` · 탭 · Enter) - 상한을 두면 그 클램프가 곧 방금 지운
   * 두 번째 시각표의 부활이라, 안전장치는 상한이 아니라 그 문이다.
   */
  function enterStack(): void {
    phase = 'stack'
    // 앞의 아홉이 흐르는 동안 릴은 **이미 돌고 있다.** 마지막 카드에서 감속이 시작되는 형태가 아니면
    // 열 장은 "아홉 번 기다린 뒤에 뽑기가 시작된다"가 되고, 그건 길이만 늘린 것이다.
    if (stacked === 0) {
      const leadMs = tenStackTotalMs(lower().map((entry) => entry.draw.grade), prefersReducedMotion())
      gauge.leadReel(reelSpinPeriodMs(leadMs, reelSpinMs(), feature()?.draw.seq ?? 0))
    }
    // 앞 장이 있었으면 여기서 미니 칩으로 내려앉는다(칩은 개봉이 **끝난** 장의 것이다). 아홉 장이 끝났으면 주인공의
    // 개봉 전으로 넘어가되 그때도 되돌린다 - 안 그러면 릴이 도는 2.6초 내내 아홉 번째의 등급이 카드와 루트에 남는다.
    if (stacked > 0) {
      const done = lower()[stacked - 1]
      if (done !== undefined) stack.commit(stacked - 1, done)
    }
    const entry = stackEntry()
    armCard()
    if (entry === undefined) {
      enterSweep()
      return
    }
    tearCard(entry, stackPlan(entry), stacked + 1)
    schedule(stackStamp, stackPlan(entry).tearMs)
  }

  /** 아홉 장 중 한 장의 스탬프. 피티·탄창 칩은 건드리지 않는다 - 회차의 회계는 마지막 한 장이 도맡는다. */
  function stackStamp(): void {
    const entry = stackEntry()
    if (entry === undefined) return
    const stepPlan = stackPlan(entry)
    card.stamp(stepPlan.stampMs)
    playGrade(entry)
    stacked += 1
    // 다음 장까지는 그 등급의 숨 한 번(`restMs`)이다. 합이 곧 `stepRevealMs` 하나다.
    schedule(enterStack, stepPlan.stampMs + stepPlan.restMs)
  }

  /**
   * 릴이 감속을 시작한다. 바늘도 같은 곡선·같은 길이로 따라 움직이고(CSS 변수 하나를 공유한다), 봉투는 점점
   * 빨라지며 떨린다. **셋 다 등급을 보지 않는다** - 이 구간이 네 등급 모두 같아야 긴장이 스포일러가 되지 않는다.
   */
  function enterSweep(): void {
    phase = 'sweep'
    const main = feature()
    if (main === null) return
    const spinMs = reelSpinMs()
    gauge.spinReel(spinMs)
    gauge.setNeedle(main.draw.gradeRoll, false)
    card.charge(spinMs)
    // 소리와 단계는 **각자의 예약**으로 간다. 정지로 넘어가는 것은 소리 사슬의 끝이 아니라 여기서 거는
    // 절대 시각의 예약 하나다 - 그러지 않으면 예약이 묶이는 숨은 탭에서 릴이 십수 초가 된다(머리말).
    reelSound.start(spinMs, main.draw.seq, table())
    schedule(enterHold, spinMs)
  }

  // 숨 한 박자. 떨림도 소리도 멎고 아무 일도 일어나지 않는다 - 이 정지가 긴장의 핵심이다.
  // 들어오는 순간의 착지 표식(`landReel`)은 **등급을 넘기지 않는다** - 언제나 같은 시각의 같은 빛이다.
  function enterHold(): void {
    phase = 'hold'
    gauge.landReel()
    card.hold()
    schedule(enterReveal, ENVELOPE_TIMELINE.holdEndMs - ENVELOPE_TIMELINE.sweepEndMs)
  }

  /* --- 단계 전이: 개봉 후 (등급이 정한다) --- */
  // 등급이 드러나는 첫 프레임. **SR·SSR만** 여기서 화면을 한 번 어둡게 하고 지나간다(P5. 전에는 SSR만).
  // 상승음이 여기 있어도 되는 것은 이 구간이 이미 **개봉 후**라서다 - 개봉 전은 `holdEndMs`에서 끝났고, 그
  // 구간의 소리는 네 등급이 여전히 같다(`envelope.test.ts`의 대조가 `holdEndMs - 1`까지를 본다).
  function enterReveal(): void {
    const ceremonyPlan = plan()
    if (ceremonyPlan.dimMs <= 0) {
      enterTear()
      return
    }
    phase = 'dim'
    ceremony.dim(ceremonyPlan)
    play('envelopeRiser')
    schedule(enterTear, ceremonyPlan.dimMs)
  }

  function enterTear(): void {
    phase = 'tear'
    const main = feature()
    if (main === null) return
    const ceremonyPlan = plan()
    tearCard(main, ceremonyPlan, ordered.length)
    schedule(enterStamp, ceremonyPlan.tearMs)
  }

  function enterStamp(): void {
    phase = 'stamp'
    const main = feature()
    const view = lastView
    if (main === null || view === null) return
    card.stamp(plan().stampMs)
    paintPity('after')
    playGrade(main)

    // 탄창이면 상단 칩으로 날아가 숫자가 오른다. 실제로 들어간 수만 - 넘친 몫은 핀이 되어 카드의 한 줄이 말한다.
    if (main.magazinesGranted > 0) head.bumpMagazines(main.magazinesGranted)
    head.setMagazines(view.magazinesAfter)
    schedule(enterDone, plan().stampMs)
  }

  function enterDone(): void {
    phase = 'done'
    clearAll()
    const view = lastView
    if (view === null) return
    root.classList.add('is-done')
    // 건너뛸 것이 남아 있지 않다 - 아무 일도 하지 않는 버튼은 고장으로 읽힌다.
    hint.hidden = true
    skip.hidden = true
    actions.showDone({ next: view.next, replay: view.replay === true })
  }

  // 어느 시각에서든 곧바로 끝 상태로. 탭·[건너뛰기]·움직임 줄이기가 여기로 온다. **결과는 한 글자도 달라지지
  // 않고** 달라지는 것은 움직임뿐이다(`ceremony.settle` - 등급 색만 남고 폭죽도 오라도 없다).
  function jumpToDone(): void {
    const main = feature()
    const view = lastView
    if (main === null || view === null) return
    clearAll()
    head.pins.set(view.pinsAfter)
    gauge.settleReel()
    gauge.setNeedle(main.draw.gradeRoll, true)
    stack.flipAll(lower())
    card.paintTexts(main, ordered.length, ordered.length)
    card.jumpToDone(main.draw.grade)
    ceremony.settle(main.draw.grade, plan())
    paintPity('after')
    head.setMagazines(view.magazinesAfter)
    enterDone()
  }

  return {
    show(view: EnvelopeView): void {
      clearAll()
      lastView = view
      ordered = revealOrder(view.cards)
      phase = 'idle'
      gauge.paintBand(table())
      resetVisual()
      paintTexts()
      paintPity('before')

      if (ordered.length === 0) {
        // 그릴 카드가 없다. 빈 연출을 돌리는 대신 곧바로 버튼을 내준다 - 닫는 길은 언제나 있어야 한다.
        enterDone()
        return
      }
      if (prefersReducedMotion()) {
        // 컷. 소리는 남긴다 - 움직임 줄이기는 눈의 설정이지 귀의 설정이 아니다.
        play('envelopeOpen')
        const main = feature()
        if (main !== null) playGrade(main)
        jumpToDone()
        return
      }
      enterRise()
    },

    stop(): void {
      clearAll()
      phase = 'idle'
    },

    jumpToDone,
    current: () => phase,
    hasView: () => lastView !== null,
    paintTexts,
  }
}
